import React, { useState, useMemo, useContext, useEffect } from "react"; // Added useEffect
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  ButtonGroup
} from "reactstrap";
import Select from "react-select";
import { Line } from "react-chartjs-2";
import placeholder from "assets/img/placeholder.jpg";
import { DataContext } from "../context/DataContext";
import { createPromotion, updatePromotion } from "../context/api";
import { s3ImageUrl } from "../utils/s3Url"; // Ensure this import is present
import { API_BASE } from "../config/api";

const Promotions = () => {
  const { data, setData, loading } = useContext(DataContext);
  const initialPromotions = data.promotions || [];
  const customers = data.customers || [];
  const isSuperuser = data.userProfile?.is_superuser === true;
  // Main state for promotions
  // const [promotions, setPromotions] = useState(initialPromotions);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [sortBy, setSortBy] = useState("label");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hover, setHover] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null); 
  const [labelError, setLabelError] = useState(""); 
  const [imageSrcForDetailModal, setImageSrcForDetailModal] = useState(placeholder); // New state for detail modal image


  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  // Add state for archive confirmation modal
  const [archiveConfirmModalOpen, setArchiveConfirmModalOpen] = useState(false);

  // Helper: normalize various date formats to YYYY-MM-DD for <input type="date">
  const toDateInputValue = (d) => {
    if (!d) return "";
    if (typeof d === "string") {
      // accept "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss" – take first 10 chars
      if (d.length >= 10) return d.slice(0, 10);
      const parsed = new Date(d);
      if (!isNaN(parsed)) return parsed.toISOString().slice(0, 10);
      return "";
    }
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    return "";
  };

  const products = data.products || [];

  const monthOptions = useMemo(() => {
    const fmt = (iso) =>
      new Date(iso).toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    const months = data.promotions.map((p) => fmt(p.createdDate));
    return Array.from(new Set(months));
  }, [data.promotions]);

  const brandOptions = useMemo(() => {
    if (!data.brands) return [];
    const uniqueBrands = Array.from(
      new Map(
        data.brands
          .filter(brand => brand && brand.name && brand.name.trim() !== "" && !brand.is_archived)
          .map(brand => [brand.id, brand])
      ).values()
    );
    return uniqueBrands.map(brand => ({ value: brand.id, label: brand.name.trim() }));
  }, [data.brands]);

  const bannerOptions = useMemo(() => {
    return (data.banners || []).filter(banner => !banner.is_archived);
  }, [data.banners]);
  // console.log("Banner options:", bannerOptions);
  // Form state
  const [formData, setFormData] = useState({
    id: null,
    pictureUrl: "",
    label: "",
    description: "",
    allCustomers: true,
    customerTargeting: "all",
    productTargeting: "all",
    products: [],
    brandId: null,
    customerIds: [],
    bannerIds: [],
    promotionalDocUrl: "",
    archived: false,
    startDate: toDateInputValue(new Date())
  });

  
  // Extract unique brands and categories from promotions for filters
  const brands = useMemo(() => {
    const brandSet = new Set((data.promotions || []).map(promo => {
      const brandMatch = promo.label.match(/^([^:]+):/);
      return brandMatch ? brandMatch[1].trim() : "Other";
    }));
    return Array.from(brandSet);
  }, [data.promotions]);


    // Effect to update image source for detail modal when formData or modal visibility changes
  useEffect(() => {
    if (detailModalOpen && formData) {
      let newSrc = placeholder; // Default to placeholder
     if (
       formData.pictureUrl &&
       (formData.pictureUrl.startsWith('blob:') ||
        formData.pictureUrl.startsWith('data:'))
     ) {        // Priority 1: Newly uploaded image (blob URL)
        newSrc = formData.pictureUrl;
      } else if (formData.id) {
        // Priority 2: Existing promotion with an ID (construct S3 URL)
        newSrc = s3ImageUrl("promotions", formData.id, ".png");
      } else if (formData.pictureUrl && typeof getImageUrl === 'function') {
        // Priority 3: Fallback for older, potentially local asset paths if getImageUrl is available
        newSrc = getImageUrl(formData.pictureUrl);
      } else if (formData.pictureUrl && !formData.pictureUrl.startsWith('blob:')) {
        // If pictureUrl is present but not a blob and no ID (e.g. initial state for add modal with a default string)
        newSrc = formData.pictureUrl; 
      }
      setImageSrcForDetailModal(newSrc);
    } else if (!detailModalOpen) {
      // Reset to placeholder when modal is closed to avoid showing stale image if modal reopens with different item
      setImageSrcForDetailModal(placeholder);
    }
  }, [formData, detailModalOpen]); // s3ImageUrl and getImageUrl are stable imports, not typically needed in deps unless they change

  // Filtered and sorted promotions
  const filteredPromotions = useMemo(() => {
    console.log("promotions:", data.promotions);
    return data.promotions
      .filter(promo => {
        // 1) Archived
        if (!showArchived && promo.archived) return false;
  
        // 2) Search text
        if (
          searchQuery &&
          !promo.label.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !promo.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
          return false;
  
        // 3) Brand
        if (selectedBrand && !promo.label.startsWith(selectedBrand)) return false;
  
        // 4) Category
        if (selectedCategory) {
          const category = selectedCategory.toLowerCase();
          const matchesCategory =
            promo.label.toLowerCase().includes(category) ||
            promo.description.toLowerCase().includes(category);
          if (!matchesCategory) return false;
        }
  
        // 5) Month
        if (selectedMonth) {
          // derive "Month YYYY" from createdDate
          const promoDate = new Date(promo.createdDate);
          const promoMonth = promoDate.toLocaleString('default', {
            month: 'long',
            year: 'numeric'
          });
          if (promoMonth !== selectedMonth) return false;
        }
  
        return true;
      })
      .sort((a, b) => {
        const aValue = a[sortBy] || '';
        const bValue = b[sortBy] || '';
        if (sortOrder === "asc") {
          return aValue.localeCompare(bValue);
        }
        return bValue.localeCompare(aValue);
      });
  }, [
    data.promotions,
    searchQuery,
    selectedBrand,
    selectedCategory,
    showArchived,
    selectedMonth,    // ← make sure to include this
    sortBy,
    sortOrder
  ]);
  

  // Modal handlers
  const openDetailModal = (promotion) => {
    setSelectedPromotion(promotion);
    setLabelError(""); // Clear error when opening detail modal

    // Determine customerTargeting based on promotion data
    let customerTargetingValue = "all";
    if (promotion.allCustomers === false || promotion.allCustomers === null) { // Check for explicit false or null
      if (promotion.customerIds && promotion.customerIds.length > 0) {
        customerTargetingValue = "select-customers";
      } else if (promotion.bannerIds && promotion.bannerIds.length > 0) {
        customerTargetingValue = "select-banners";
      }
    }

    const products = promotion.products; // Standardize to products

    // Determine productTargeting
    let productTargetingValue = "all";
    if (products && products.length > 0) {
      productTargetingValue = "select-products";
    } else if (promotion.brandId) {
      productTargetingValue = "select-brand";
    }

    // Resolve start date: prefer explicit startDate, otherwise fallback to createdDate
    const resolvedStartDate = toDateInputValue(promotion.startDate || promotion.createdDate || new Date());

    setFormData({
      ...promotion,
      customerTargeting: customerTargetingValue,
      productTargeting: productTargetingValue,
      // Ensure bannerIds and customerIds are arrays, even if null/undefined in promotion data
      bannerIds: promotion.bannerIds || [], 
      customerIds: promotion.customerIds || [],
      products: products || [],
      startDate: resolvedStartDate,
    });
    setIsEditing(false);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedPromotion(null);
    setDetailModalOpen(false);
    setIsEditing(false);
    setSelectedImageFile(null); // Clear selected image file
    setLabelError(""); // Clear label error
  };

  const openAddModal = () => {
    setFormData({
      id: null, // Changed from Date.now() as backend will assign
      pictureUrl: "",
      label: "",
      description: "",
      allCustomers: true,
      customerTargeting: "all",
      productTargeting: "all",
      products: [],
      brandId: null,
      customerIds: [],
      bannerIds: [],
      promotionalDocUrl: "",
      archived: false,
      startDate: toDateInputValue(new Date())
    });
    setSelectedImageFile(null); // Clear selected image file
    setLabelError(""); // Clear label error
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setSelectedImageFile(null); // Clear selected image file
    setLabelError(""); // Clear label error
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (name === "label") {
      setLabelError(""); // Clear error when user types in label
    }
  };

  const handleCustomerSelection = (customerId) => {
    setFormData(prev => {
      const newCustomerIds = prev.customerIds.includes(customerId)
        ? prev.customerIds.filter(id => id !== customerId)
        : [...prev.customerIds, customerId];
      return {
        ...prev,
        customerIds: newCustomerIds
      };
    });
  };

  const handleProductSelection = (productId) => {
    setFormData(prev => {
      const newProducts = prev.products.includes(productId)
        ? prev.products.filter(id => id !== productId)
        : [...prev.products, productId];
      return {
        ...prev,
        products: newProducts
      };
    });
  };

  const handleDocUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, using a blob URL. In a real app, this would be uploaded to a server.
      const docUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        promotionalDocUrl: docUrl
      }));
      // If you also need to store the file object for later S3 upload (similar to images):
      // setSelectedDocFile(file); // You would need to add a new state: const [selectedDocFile, setSelectedDocFile] = useState(null);
    }
  };

  const sortedCustomers = useMemo(() => {
  return [...customers]
    .filter(customer => !customer.archived) // Filter out archived customers
    .sort((a, b) => {
    const aName = a.businessName.toLowerCase();
    const bName = b.businessName.toLowerCase();
    const aIsNum = /^\d/.test(aName);
    const bIsNum = /^\d/.test(bName);
    if (aIsNum && !bIsNum) return -1;   // digits first
    if (!aIsNum && bIsNum) return 1;    // non-digits after
    return aName.localeCompare(bName);  // then alphabetical
  });
}, [customers]);

const sortedProducts = useMemo(() => {
  return [...products]
    .filter(p => !p.is_archived)
    .sort((a, b) => a.name.localeCompare(b.name));
}, [products]);


  // Convert JPEG to PNG via canvas
  function convertToPng(file) {
    return new Promise((resolve, reject) => {
      if (!file || !file.type) {
        return reject(new Error("Invalid file object."));
      }
      const isSupportedType = /^image\/(jpeg|jpg|png)$/i.test(file.type);
      if (!isSupportedType) {
        return reject(new Error("File type not supported. Please upload JPG or PNG."));
      }
      if (/^image\/png$/i.test(file.type)) { // If already PNG
        return resolve(file); 
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error("Canvas to Blob conversion failed"));
              }
              const pngFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.png',
                { type: 'image/png' }
              );
              resolve(pngFile);
            },
            'image/png'
          );
        };
        img.onerror = (err) => reject(new Error(`Image load error: ${err.message || err}`));
        img.src = reader.result;
      };
      reader.onerror = (err) => reject(new Error(`File reader error: ${err.message || err}`));
      reader.readAsDataURL(file);
    });
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const pngFile = await convertToPng(file);
      setSelectedImageFile(pngFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          pictureUrl: reader.result // Local blob/data URL for preview
        }));
      };
      reader.readAsDataURL(pngFile);
    } catch (err) {
      console.error('Image processing failed:', err);
      alert(`Image processing failed: ${err.message}`);
      setSelectedImageFile(null);
      
      let fallbackImageUrl = placeholder;
      if (isEditing && selectedPromotion) {
        // If editing, fallback to the promotion's current S3 URL (if it has one and an ID) or its stored pictureUrl
        if (selectedPromotion.id) {
            fallbackImageUrl = s3ImageUrl("promotions", selectedPromotion.id, ".png");
        } else if (selectedPromotion.pictureUrl && !selectedPromotion.pictureUrl.startsWith('blob:')){
            fallbackImageUrl = selectedPromotion.pictureUrl;
        } // If it's a new promotion being edited (no ID yet) or pictureUrl is already a blob, placeholder is fine.
      }
      setFormData(prev => ({ ...prev, pictureUrl: fallbackImageUrl }));
    }
  };

  // Upload to S3 (Adapted from User Example)
  async function uploadToS3Internal(file, promotionId) {
    const API = API_BASE;
    
    const presignedUrlPayload = {
      fileType: file.type,       // e.g., 'image/png'
      folder: 'promotions',
      userId: String(promotionId) // Backend expects userId to form part of the key
    };

    const res = await fetch(`${API}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(presignedUrlPayload),
    });

    if (!res.ok) {
      let errorMessage = `HTTP error ${res.status}`;
      try {
        const errorResponse = await res.json(); // Try to parse JSON error from backend
        errorMessage = errorResponse.message || errorMessage; // Use backend message if available
      } catch (e) {
        // If parsing JSON fails, try to get raw text or use default HTTP error
        try {
            const textError = await res.text();
            if (textError) errorMessage = textError; // Use text error if available and not empty
        } catch (textE) {
            // Fallback if res.text() also fails, errorMessage remains HTTP error status
        }
      }
      throw new Error(`Failed to get upload URL: ${res.status} ${errorMessage}`);
    }
    
    const { uploadUrl, key: returnedKey } = await res.json();

    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      console.error('S3 PUT status:', uploadRes.status, errorText);
      throw new Error(`Failed to upload file to S3: ${uploadRes.status} - ${errorText}`);
    }

    const bucket = process.env.REACT_APP_S3_BUCKET;
    const region = process.env.REACT_APP_AWS_REGION;
    if (!bucket || !region) {
        console.error("S3 bucket or region not configured in .env file.");
        throw new Error("S3 configuration missing.");
    }
    return `https://${bucket}.s3.${region}.amazonaws.com/${returnedKey}`;
  }

// Save (add or edit) a promotion
const handleSavePromotion = async () => {
  if (!formData.label || formData.label.trim() === "") {
    setLabelError("Please enter a promotional label");
    return;
  }
  setLabelError(""); 

  const currentSelectedFile = selectedImageFile; 

  if (isEditing) {
    const originalPromotionForRollback = data.promotions.find(p => p.id === formData.id);
    let payloadForBackend = { ...formData }; // Contains text changes. pictureUrl is current preview (blob or old S3).

    // Optimistic UI: Show current formData (local blob preview if new image selected)
    setData(d => ({
      ...d,
      promotions: d.promotions.map(p =>
        p.id === formData.id ? { ...p, ...formData } : p 
      )
    }));

    if (currentSelectedFile) {
      try {
        const newS3Url = await uploadToS3Internal(currentSelectedFile, formData.id);
        // Add a cache-busting query parameter
        const finalS3Url = `${newS3Url}?timestamp=${new Date().getTime()}`;
        payloadForBackend.pictureUrl = finalS3Url; // Update payload with the new S3 URL (with cache buster)

        // Update UI optimistically with the final S3 URL
        setData(d => ({
          ...d,
          promotions: d.promotions.map(p =>
            p.id === formData.id ? { ...p, ...formData, pictureUrl: finalS3Url } : p
          )
        }));

      } catch (error) {
        console.error("S3 upload failed for existing promotion:", error);
        alert(`Image upload failed: ${error.message}. Text changes will be saved, but the image will not be updated.`);
        // Revert pictureUrl in payloadForBackend to the original S3 URL, not the blob
        payloadForBackend.pictureUrl = originalPromotionForRollback?.pictureUrl || placeholder; 
      }
    } else {
      // No new file, ensure payloadForBackend.pictureUrl is the existing S3 URL (already in formData)
      // If it somehow became a blob (e.g. from a previous failed attempt without page refresh), revert.
      if (payloadForBackend.pictureUrl && payloadForBackend.pictureUrl.startsWith('blob:')) {
          payloadForBackend.pictureUrl = originalPromotionForRollback?.pictureUrl || placeholder;
      }
    }

    try {
      const updatedPromotionFromAPI = await updatePromotion(formData.id, payloadForBackend);
      // Final UI update with backend-confirmed data
   // Final UI update with backend-confirmed data (use the real S3 URL, not our util)
   setData(d => ({
    ...d,
     promotions: d.promotions.map(p =>
       p.id === formData.id
         ? { ...p, ...updatedPromotionFromAPI, pictureUrl: updatedPromotionFromAPI.pictureUrl }
         : p
     )
   }));
      setSelectedImageFile(null);
      closeDetailModal();
    } catch (err) {
      console.error("Failed to update promotion:", err);
      alert(`Failed to update promotion: ${err.message}`);
      // Rollback UI to original state before optimistic changes if API call fails
      if (originalPromotionForRollback) {
        setData(d => ({ ...d, promotions: d.promotions.map(p => p.id === formData.id ? originalPromotionForRollback : p) })); 
      }
    }
  } else { // Adding new promotion
    const tempId = Date.now();
    const optimisticNewPromotion = {
      ...formData,
      id: tempId,
      pictureUrl: formData.pictureUrl || placeholder, 
    };
    setData(d => ({ ...d, promotions: [...d.promotions, optimisticNewPromotion] }));

    try {
      let payloadForCreate = { ...formData };
      delete payloadForCreate.id;
      payloadForCreate.pictureUrl = ""; // Create initially without image URL

      const savedPromotionFromBackend = await createPromotion(payloadForCreate);
      let finalPromotionDataForUI = { ...savedPromotionFromBackend };

      if (currentSelectedFile) {
        try {
          const s3Url = await uploadToS3Internal(currentSelectedFile, savedPromotionFromBackend.id);
          const updatedPromotionWithImage = await updatePromotion(savedPromotionFromBackend.id, { ...finalPromotionDataForUI, pictureUrl: s3Url });
          finalPromotionDataForUI = updatedPromotionWithImage;
        } catch (uploadError) {
          console.error("S3 upload failed for new promotion:", uploadError);
          alert(`Promotion created (ID: ${savedPromotionFromBackend.id}), but image upload failed: ${uploadError.message}. You may need to edit it.`);
        }
      }
      
      setData(d => ({
        ...d,
        promotions: d.promotions.map(p => (p.id === tempId ? finalPromotionDataForUI : p))
      }));
      setSelectedImageFile(null); 
      closeAddModal();
    } catch (err) {
      console.error("Failed to create promotion:", err);
      alert(`Failed to create promotion: ${err.message}`);
      setData(d => ({ ...d, promotions: d.promotions.filter(p => p.id !== tempId) }));
    }
  }
};

  const getBrandName = (brandId) => {
    const productWithBrand = products.find(p => p.brand?.id === brandId);
    return productWithBrand?.brand?.name || "Unbranded";
  };

  // Handlers for archive confirmation modal
  const openArchiveConfirmModal = () => setArchiveConfirmModalOpen(true);
  const closeArchiveConfirmModal = () => setArchiveConfirmModalOpen(false);

  const handleConfirmArchivePromotion = async () => {
    if (!selectedPromotion) return;

    const newArchivedStatus = !formData.archived;
    const updatedPromotionData = { ...formData, archived: newArchivedStatus };

    // Optimistic UI update
    const originalPromotions = data.promotions;
    setData(d => ({
      ...d,
      promotions: d.promotions.map(p =>
        p.id === selectedPromotion.id ? updatedPromotionData : p
      ),
    }));
    setFormData(updatedPromotionData); // Update form data as well

    try {
      await updatePromotion(selectedPromotion.id, { archived: newArchivedStatus });
      // API call successful
    } catch (error) {
      console.error(`Failed to ${newArchivedStatus ? "archive" : "unarchive"} promotion:`, error);
      // Revert optimistic update
      setData(d => ({
        ...d,
        promotions: originalPromotions,
      }));
      setFormData(originalPromotions.find(p => p.id === selectedPromotion.id) || formData);
      // Optionally, display an error message
    }

    closeArchiveConfirmModal();
    closeDetailModal(); // Close the main edit modal as well
  };


  const handleArchivePromotion = (id) => {
    // This function is now replaced by the confirmation flow
    // but kept here in case any other part of the code still calls it directly.
    // For new archive actions, openArchiveConfirmModal should be used.
    console.warn("handleArchivePromotion directly called, should use confirmation flow");
    const promotionToUpdate = data.promotions.find(promo => promo.id === id);

    if (promotionToUpdate) {
      setFormData({...promotionToUpdate, archived: !promotionToUpdate.archived}); // Set up formData for confirmation
      setSelectedPromotion(promotionToUpdate);
      openArchiveConfirmModal();
    }
  };

  // Handle image error
  const handleImageError = (e) => {
    e.target.src = placeholder;
  };

  const images = require.context("assets/img", false, /\.(png|jpe?g|svg)$/);

  const getImageUrl = (url) => {
    if (!url) return placeholder; // fallback if URL is empty
    // Extract the file name from the URL (assumes URL is like "assets/img/monford.png")
    const parts = url.split("/");
    const fileName = parts[parts.length - 1];
    try {
      return images(`./${fileName}`);
    } catch (err) {
      return placeholder;
    }
  };

  const getDownloadFileName = (url) => {
    if (!url) return "download";
    return url.split("/").pop();
  };

  
  return (
    <div className="content">
      {/* Filters Row */}
      <Row className="mb-3">

        <Col md="2">
          <FormGroup>
            <Label for="sortBy">Sort By</Label>
            <Input
              type="select"
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="label">Name</option>
              <option value="description">Description</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md="2">
            <FormGroup>
              <Label for="monthFilter">Month</Label>
              <Input
                type="select"
                id="monthFilter"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {monthOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        <Col md="3" className="d-flex align-items-end">
          <FormGroup check>
            <Label
              check
              className={`p-2 rounded ${
                showArchived ? "bg-secondary text-white" : "bg-light text-dark"
              }`}
            >
              <Input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />{" "}
              {showArchived ? "Remove Archived" : "Show Archived"}
            </Label>
          </FormGroup>

        </Col>
          {isSuperuser && (
        <Col
  md="4"
  className="ml-auto" // Bootstrap class for left margin auto
  style={{
    cursor: "pointer",
    transition: "background-color 0.3s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    borderRadius: "0.25rem",
    backgroundColor: hover ? "#f8f9fa" : "transparent"
  }}
  onClick={openAddModal}
  onMouseEnter={() => setHover(true)}
  onMouseLeave={() => setHover(false)}
>
  <i
    className="nc-icon nc-tag-content"
    style={{ fontSize: "1.5rem" }}
    title="Add New Product"
  />
  <p
    style={{
      textAlign: "center",
      marginTop: "0.25rem",
      textTransform: "none",
      margin: 0
    }}
  >
    Add New Promotion
  </p>
</Col>
          )}
      </Row>

      {/* Promotions Grid */}
      <Row>
        {filteredPromotions.map((promo) => (
          <Col key={promo.id} md="3" className="mb-2">
              <Card 
                onClick={() => openDetailModal(promo)} 
                style={{ cursor: "pointer" }}
                className="d-flex flex-column h-100"
              >
                {/* Image container with A4 aspect ratio */}
                <div style={{ flex: "0 0 auto", aspectRatio: "1 / 1.414", overflow: "hidden" }}>
                  <img
                    src={promo.pictureUrl || s3ImageUrl("promotions", promo.id, ".png")} // Use s3ImageUrl
                    alt={promo.label}
                    className="card-img-top"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop if placeholder also fails
                      e.target.src = placeholder;
                    }}
                  />
                </div>
              <CardBody className="d-flex flex-column">
                <h5 className="card-title text-truncate">{promo.label}</h5>
                <p className="card-text flex-grow-1" style={{ 
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: "3",
                  WebkitBoxOrient: "vertical"
                }}>{promo.description}</p>
                <div className="d-flex justify-content-between align-items-center mt-auto">
                  <span className={`badge ${promo.allCustomers ? "bg-success" : "bg-info"}`}>
                    {promo.allCustomers ? "All Customers" : "Selected Customers"}
                  </span>
                  {promo.archived && <span className="badge bg-secondary">Archived</span>}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Detail/Edit Modal */}
      <Modal isOpen={detailModalOpen} toggle={closeDetailModal} size="lg">
        <ModalHeader toggle={closeDetailModal}>
          {isEditing ? "Edit Promotion" : "Promotion Details"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="4">
              <div style={{ aspectRatio: "1 / 1.414", overflow: "hidden" }}>
                <img
                  src={imageSrcForDetailModal} // Use the dedicated state for the image source
                  alt={formData.label || "Promotion image"}
                  className="card-img-top"
                  style={{ width: "100%", height: "100%", objectFit: "cover", cursor: (isEditing && !formData.archived) ? "default" : "zoom-in" }}
                  onClick={() => {
                    // Allow zoom if not editing (view mode) or if editing but archived (image is view-only)
                    if (!(isEditing && !formData.archived)) { 
                      setModalImageUrl(imageSrcForDetailModal); // Use the current determined source for zoom
                      setImageModalOpen(true);
                    }
                  }}
                  onError={(e) => {
                    // If the current src (imageSrcForDetailModal) fails to load, explicitly set it to placeholder
                    if (imageSrcForDetailModal !== placeholder) { 
                      console.warn("Image failed to load in detail modal, falling back to placeholder. Source was:", imageSrcForDetailModal);
                      setImageSrcForDetailModal(placeholder);
                    }
                  }}
                />
              </div>
              {isEditing && (
                <FormGroup>
                  <Label for="promotionImage">Update Image</Label>
                  <Input
                    type="file"
                    id="promotionImage"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={formData.archived}
                  />
                </FormGroup>
              )}
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="promotionLabel">Promotion Label</Label>
                <Input
                  type="text"
                  id="promotionLabel"
                  name="label"
                  value={formData.label}
                  onChange={handleFormChange}
                  disabled={!isEditing || formData.archived}
                />
                {isEditing && labelError && <div style={{ color: "red", fontSize: "0.8rem", marginTop: "0.25rem" }}>{labelError}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="promotionDescription">Description</Label>
                <Input
                  type="textarea"
                  id="promotionDescription"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  disabled={!isEditing || formData.archived}
                />
              </FormGroup>
              <FormGroup>
                <Label for="promotionStartDate">Start Date</Label>
                <Input
                  type="date"
                  id="promotionStartDate"
                  name="startDate"
                  value={formData.startDate || ""}
                  onChange={handleFormChange}
                  disabled={!isEditing || formData.archived}
                />
              </FormGroup>
              <FormGroup>
                  <Label for="brand">Brand</Label>
                    <Select
                      id="brand"
                      name="brandId"
                      value={brandOptions.find(opt => opt.value == formData.brandId)}
                      onChange={(selected) => {
                        const newBrandId = selected?.value;
                        setFormData(prev => ({
                          ...prev,
                          brandId: newBrandId,
                          // Reset productTargeting & productIds whenever brand changes:
                          productTargeting: "all",
                          products: []
                        }));
                      }}
                      options={brandOptions}
                      placeholder="Select Brand"
                      isDisabled={!isEditing || formData.archived}  // or remove isDisabled in “Add” modal
                    />
                </FormGroup>
                {formData.brandId && (
  <FormGroup>
    <Label for="productTargeting">Product Targeting</Label>
    <Input
      type="select"
      id="productTargeting"
      value={formData.productTargeting}
      onChange={e => {
        setFormData(prev => ({
          ...prev,
          productTargeting: e.target.value,
          // If switching back to “all”, clear productIds:
          products: e.target.value === "all" ? [] : prev.products
        }));
      }}
      disabled={!isEditing || formData.archived /* or remove for Add Modal */}
    >
      <option value="all">All Products for this Brand</option>
      <option value="select-products">Select Products</option>
    </Input>
  </FormGroup>
)}
{formData.brandId && formData.productTargeting === "select-products" && (
  <FormGroup>
    <Label>Select Products</Label>
    <div
      style={{ maxHeight: "200px", overflowY: "auto", opacity: formData.archived ? 0.6 : 1 }}
      className="border rounded p-2"
    >
      {products
        .filter(p => p.brand && p.brand.id == formData.brandId)
        .map(product => {
          const isSelected = formData.products.includes(product.id);
          return (
            <div
              key={product.id}
              className={`form-check p-2 rounded ${
                isSelected ? "bg-info text-white" : "bg-light"
              }`}
              style={{ cursor: formData.archived ? "not-allowed" : "pointer" }}
              onClick={() => {
                if (formData.archived) return;
                // toggle this product’s ID in formData.productIds
                setFormData(prev => {
                  const nextIds = prev.products.includes(product.id)
                    ? prev.products.filter(id => id !== product.id)
                    : [...prev.products, product.id];
                  return { ...prev, products: nextIds };
                });
              }}
            >
              <input
                type="checkbox"
                className="form-check-input me-2"
                id={`promo-product-${product.id}`}
                checked={isSelected}
                readOnly
                disabled={formData.archived}
              />
              <span className="form-check-label">
                {product.name}
              </span>
            </div>
          );
        })}
      {/*
        If the brand has zero products, show a “No products” message:
      */}
      {products.filter(p => p.brand?.id === formData.brandId).length === 0 && (
        <div className="text-muted">No products found for this brand.</div>
      )}
    </div>
  </FormGroup>
)}
                <FormGroup>
  <Label for="customerTargeting">Customer Targeting</Label>
  <Input
    type="select"
    id="customerTargeting"
    value={formData.customerTargeting || "all"}
    onChange={(e) => {
      const newTargeting = e.target.value;
      setFormData((prev) => ({
        ...prev,
        customerTargeting: newTargeting,
        allCustomers: newTargeting === "all",
        // Clear other targeting arrays based on selection
        customerIds: newTargeting === "select-banners" || newTargeting === "all" ? [] : prev.customerIds,
        bannerIds: newTargeting === "select-customers" || newTargeting === "all" ? [] : prev.bannerIds,
      }));
    }}
    disabled={!isEditing || formData.archived}
  >
    <option value="all">All Customers</option>
    <option value="select-customers">Select Customers</option>
    <option value="select-banners">Select Banners</option>
  </Input>
</FormGroup>

{formData.customerTargeting === "select-customers" && (
  <FormGroup>
    <Label>Select Customers</Label>
    <div style={{ maxHeight: "200px", overflowY: "auto", opacity: (!isEditing || formData.archived) ? 0.6 : 1 }} className="border rounded p-2">
      {sortedCustomers.map((customer) => { // sortedCustomers is already filtered
        const isSelected = formData.customerIds.includes(customer.id);
        return (
          <div
            key={customer.id}
            className={`form-check p-2 rounded ${isSelected ? "bg-info text-white" : "bg-light"}`}
            style={{ cursor: (!isEditing || formData.archived) ? "not-allowed" : "pointer" }}
            onClick={() => {
              if (!isEditing || formData.archived) return;
              handleCustomerSelection(customer.id);
            }}
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`new-customer-${customer.id}`}
              checked={isSelected}
              onChange={() => {
                if (!isEditing || formData.archived) return; 
                handleCustomerSelection(customer.id);
              }}
              disabled={!isEditing || formData.archived}
            />
            <label className="form-check-label" htmlFor={`new-customer-${customer.id}`}>
              {customer.businessName}
            </label>
          </div>
        );
      })}
    </div>
  </FormGroup>
)}

{formData.customerTargeting === "select-banners" && (
  <FormGroup>
    <Label>Select Banners</Label>
    <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto", opacity: (!isEditing || formData.archived) ? 0.6 : 1 }}>
      {bannerOptions.map((banner) => {  // bannerOptions is already filtered
        const isSelected = (formData.bannerIds || []).includes(banner.name); 
        return (
          <div
            key={banner.id} // Use banner.id as key
            className={`form-check p-2 rounded ${isSelected ? "bg-info text-white" : "bg-light"}`}
            style={{ cursor: (!isEditing || formData.archived) ? "not-allowed" : "pointer" }}
            onClick={() => {
                if (!isEditing || formData.archived) return;
                setFormData((prev) => {
                  const current = prev.bannerIds || [];
                  const updated = current.includes(banner.name) // Use banner.name for toggling
                    ? current.filter((b) => b !== banner.name)
                    : [...current, banner.name];
                  return { ...prev, bannerIds: updated };
                });
              }
            }
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`edit-banner-${banner.id}`} 
              checked={isSelected}
              readOnly 
              disabled={!isEditing || formData.archived}
            />
            <label className="form-check-label" htmlFor={`edit-banner-${banner.id}`}>
              {banner.name} {/* Display banner.name */}
            </label>
          </div>
        );
      })}
    </div>
  </FormGroup>
)}


              {isEditing && (
                <FormGroup>
                  <Label for="promotionalDoc">Update Promotional Document</Label>
                  <Input
                    type="file"
                    id="promotionalDoc"
                    accept=".pdf,.doc,.docx"
                    onChange={handleDocUpload}
                    disabled={formData.archived}
                  />
                </FormGroup>
              )}
{formData.id && (
  <div className="mt-2">
    <a
      href={s3ImageUrl("promotions", formData.id, ".png")}
      download={`${formData.label || "promotion"}.png`}
      className="btn btn-link p-0"
      style={{ fontSize: "0.6rem" }}
    >
      Download Promotional Image
    </a>
  </div>
)}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          {isEditing && ( // Show archive/unarchive button only in edit mode
            <Button
              color={formData.archived ? "success" : "danger"}
              style={{
                backgroundColor: formData.archived ? 'lightgreen' : 'lightcoral',
                marginRight: 'auto'
              }}
              onClick={openArchiveConfirmModal}
            >
              {formData.archived ? "Unarchive" : "Archive"}
            </Button>
          )}
          {!isEditing && isSuperuser && (
            <Button color="primary" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
          {/* Removed old direct archive button from here */}
          {isEditing && (
            <Button color="primary" onClick={handleSavePromotion}>
              Save Changes
            </Button>
          )}
          <Button color="secondary" onClick={closeDetailModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Modal */}
      <Modal isOpen={addModalOpen} toggle={closeAddModal} size="lg">
        <ModalHeader toggle={closeAddModal}>Add New Promotion</ModalHeader>
        <ModalBody>
          <Row>
            <Col md="4">
              <div style={{ height: "300px", overflow: "hidden", marginBottom: "1rem" }}>
                <img
                  src={formData.pictureUrl || placeholder}
                  alt="Promotion"
                  className="img-fluid w-100 h-100"
                  style={{ objectFit: "cover" }}
                  onError={handleImageError}
                />
              </div>
              <FormGroup>
                <Label for="newPromotionImage">Upload Image</Label>
                <Input
                  type="file"
                  id="newPromotionImage"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="newPromotionLabel">Promotion Label</Label>
                <Input
                  type="text"
                  id="newPromotionLabel"
                  name="label"
                  value={formData.label}
                  onChange={handleFormChange}
                  placeholder="e.g., Brand Name: Promotion Title"
                />
                {labelError && <div style={{ color: "red", fontSize: "0.8rem", marginTop: "0.25rem" }}>{labelError}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="newPromotionDescription">Description</Label>
                <Input
                  type="textarea"
                  id="newPromotionDescription"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Enter promotion details..."
                />
              </FormGroup>
              <FormGroup>
                <Label for="newPromotionStartDate">Start Date</Label>
                <Input
                  type="date"
                  id="newPromotionStartDate"
                  name="startDate"
                  value={formData.startDate || ""}
                  onChange={handleFormChange}
                />
              </FormGroup>
              {/* <FormGroup>
                  <Label for="brand">Brand</Label>
                  <Select
                    id="brand"
                    name="brandId"
                    value={brandOptions.find(opt => opt.value === formData.brandId) || null}
                    onChange={(selected) => setFormData(prev => ({ ...prev, brandId: selected?.value || null }))}
                    options={brandOptions}
                    placeholder="Select Brand"
                  />
                </FormGroup> */}
                             <FormGroup>
                  <Label for="brand">Brand</Label>
                    <Select
                      id="brand"
                      name="brandId"
                      value={brandOptions.find(opt => opt.value == formData.brandId)}
                      onChange={(selected) => {
                        const newBrandId = selected?.value;
                        setFormData(prev => ({
                          ...prev,
                          brandId: newBrandId,
                          // Reset productTargeting & productIds whenever brand changes:
                          productTargeting: "all",
                          products: []
                        }));
                      }}
                      options={brandOptions}
                      placeholder="Select Brand"
                    />
                </FormGroup>
                {formData.brandId && (
  <FormGroup>
    <Label for="productTargeting">Product Targeting</Label>
    <Input
      type="select"
      id="productTargeting"
      value={formData.productTargeting}
      onChange={e => {
        setFormData(prev => ({
          ...prev,
          productTargeting: e.target.value,
          // If switching back to “all”, clear productIds:
          products: e.target.value === "all" ? [] : prev.products
        }));
      }}
    >
      <option value="all">All Products for this Brand</option>
      <option value="select-products">Select Products</option>
    </Input>
  </FormGroup>
)}
{formData.brandId && formData.productTargeting === "select-products" && (
  <FormGroup>
    <Label>Select Products</Label>
    <div
      style={{ maxHeight: "200px", overflowY: "auto" }}
      className="border rounded p-2"
    >
      {products
        .filter(p => p.brand && p.brand.id == formData.brandId)
        .map(product => {
          const isSelected = formData.products.includes(product.id);
          return (
            <div
              key={product.id}
              className={`form-check p-2 rounded ${
                isSelected ? "bg-info text-white" : "bg-light"
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => {
                // toggle this product’s ID in formData.productIds
                setFormData(prev => {
                  const nextIds = prev.products.includes(product.id)
                    ? prev.products.filter(id => id !== product.id)
                    : [...prev.products, product.id];
                  return { ...prev, products: nextIds };
                });
              }}
            >
              <input
                type="checkbox"
                className="form-check-input me-2"
                id={`promo-product-${product.id}`}
                checked={isSelected}
                readOnly
              />
              <span className="form-check-label">
                {product.name}
              </span>
            </div>
          );
        })}
      {/*
        If the brand has zero products, show a “No products” message:
      */}
      {products.filter(p => p.brand?.id === formData.brandId).length === 0 && (
        <div className="text-muted">No products found for this brand.</div>
      )}
    </div>
  </FormGroup>
)}
                <FormGroup>
                    <Label for="customerTargeting">Customer Targeting</Label>
                    <Input
                      type="select"
                      id="customerTargeting"
                      value={formData.customerTargeting || "all"}
                      onChange={(e) => {
                        const newTargeting = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          customerTargeting: newTargeting,
                          allCustomers: newTargeting === "all",
                          // Clear other targeting arrays based on selection
                          customerIds: newTargeting === "select-banners" || newTargeting === "all" ? [] : prev.customerIds,
                          bannerIds: newTargeting === "select-customers" || newTargeting === "all" ? [] : prev.bannerIds,
                        }));
                      }}
                    >
                      <option value="all">All Customers</option>
                      <option value="select-customers">Select Customers</option>
                      <option value="select-banners">Select Banners</option>
                    </Input>
                  </FormGroup>
{formData.customerTargeting === "select-customers" && (
  <FormGroup>
    <Label>Select Customers</Label>
    <div style={{ maxHeight: "200px", overflowY: "auto" }} className="border rounded p-2">
      {sortedCustomers.map((customer) => { // sortedCustomers is already filtered
        const isSelected = formData.customerIds.includes(customer.id);
        return (
          <div
            key={customer.id}
            className={`form-check p-2 rounded ${isSelected ? "bg-info text-white" : "bg-light"}`}
            style={{ cursor: "pointer" }}
            onClick={() => handleCustomerSelection(customer.id)}
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`new-customer-${customer.id}`}
              checked={isSelected}
              onChange={() => handleCustomerSelection(customer.id)}
            />
            <label className="form-check-label" htmlFor={`new-customer-${customer.id}`}>
              {customer.businessName}
            </label>
          </div>
        );
      })}
    </div>
  </FormGroup>
)}

{formData.customerTargeting === "select-banners" && (
  <FormGroup>
    <Label>Select Banners</Label>
    <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
      {bannerOptions.map((banner) => { // bannerOptions is already filtered
        const isSelected = (formData.bannerIds || []).includes(banner.name); // Use banner.name for checking selection
        return (
          <div
            key={banner.id} // Use banner.id as key
            className={`form-check p-2 rounded ${isSelected ? "bg-info text-white" : "bg-light"}`}
            style={{ cursor: "pointer" }}
            onClick={() =>
              setFormData((prev) => {
                const current = prev.bannerIds || [];
                const updated = current.includes(banner.name) // Use banner.name for toggling
                  ? current.filter((b) => b !== banner.name)
                  : [...current, banner.name];
                return { ...prev, bannerIds: updated };
              })
            }
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`new-banner-${banner.id}`} // Use banner.id for unique id
              checked={isSelected}
              readOnly // Checkbox is controlled by div click
            />
            <label className="form-check-label" htmlFor={`new-banner-${banner.id}`}>
              {banner.name} {/* Display banner.name */}
            </label>
          </div>
        );
      })}
    </div>
  </FormGroup>
)}
              <FormGroup>
                <Label for="newPromotionalDoc">Upload Promotional Document</Label>
                <Input
                  type="file"
                  id="newPromotionalDoc"
                  accept=".pdf,.doc,.docx"
                  onChange={handleDocUpload}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
          <Button color="primary" onClick={handleSavePromotion}>
            Add Promotion
          </Button>
          <Button color="secondary" onClick={closeAddModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={imageModalOpen} toggle={() => setImageModalOpen(false)} size="xl" centered>
  <ModalBody style={{ padding: 0 }}>
    <img
      src={modalImageUrl} // This is already set correctly by the onClick handler above
      alt="Full Screen"
      style={{ width: "100%", height: "auto", maxHeight: "90vh", objectFit: "contain" }}
      onError={(e) => { // Added onError for safety, though modalImageUrl should be valid or placeholder
        e.target.onerror = null;
        e.target.src = placeholder;
      }}
    />
  </ModalBody>
</Modal>

      {/* Archive/Unarchive Confirmation Modal */}
      {selectedPromotion && (
        <Modal isOpen={archiveConfirmModalOpen} toggle={closeArchiveConfirmModal} centered>
          <ModalHeader toggle={closeArchiveConfirmModal}>
            Confirm {formData.archived ? "Unarchive" : "Archive"} Promotion
          </ModalHeader>
          <ModalBody>
            Are you sure you want to {formData.archived ? "unarchive" : "archive"} the promotion "{formData.label}"?
          </ModalBody>
          <ModalFooter style={{ padding: '0.75rem 1rem' }}>
            <Button color={formData.archived ? "success" : "danger"} onClick={handleConfirmArchivePromotion}>
              Yes, {formData.archived ? "Unarchive" : "Archive"}
            </Button>
            <Button color="secondary" onClick={closeArchiveConfirmModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default Promotions;
